/**
 * MCP server detail panel — read-only operator view of one mcp-gateway
 * server (status, connection data, enabled tools, guardrails, and recent
 * activity). Used by McpServerDetailPage, one per server id.
 *
 * The activity list is an in-memory buffer inside the gateway: it is
 * per-instance and not persistent. The durable audit log is a separate piece
 * of work.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Descriptions,
  Space,
  Spin,
  Steps,
  Table,
  Tag,
  Typography,
} from 'antd';
import { ApiOutlined, ReloadOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import {
  fetchMcpActivity,
  fetchMcpCatalog,
  fetchMcpStatus,
} from '../../services/api.service';
import type {
  McpActivityEntry,
  McpCatalog,
  McpServerStatus,
  McpTool,
} from '../../types/mcp';
import { useMcpTenant } from '../../hooks/useMcpTenant';
import BrainIndexCard from './BrainIndexCard';
import McpAccessPanel from './McpAccessPanel';
import McpGuardrailsPanel from './McpGuardrailsPanel';

const { Title, Text, Paragraph } = Typography;

interface McpServerDetailPanelProps {
  serverId: string;
}

function StatusBadge({
  status,
  serverId,
}: {
  status: McpServerStatus | null;
  serverId: string;
}): React.ReactElement {
  if (!status) return <Badge status="error" text="No accesible" />;
  // Per-backend state; `child` is the pre-multi-backend Mongo-only field.
  const state = status.backends?.[serverId]?.status ?? (serverId === 'mongodb' ? status.child : undefined);
  if (state === 'ready') return <Badge status="success" text="Operativo" />;
  if (state === 'starting') return <Badge status="processing" text="Arrancando" />;
  if (state === undefined) return <Badge status="default" text="No ofrecido por este gateway" />;
  return <Badge status="warning" text={serverId === 'mongodb' ? 'Sin conexión a MongoDB' : 'No disponible'} />;
}

const McpServerDetailPanel: React.FC<McpServerDetailPanelProps> = ({ serverId }) => {
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<McpServerStatus | null>(null);
  const [catalog, setCatalog] = useState<McpCatalog | null>(null);
  const [activity, setActivity] = useState<McpActivityEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Everything here is per organization: the connector URL, who may use it and
  // the activity all belong to the tenant being managed.
  const tenant = useMcpTenant();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statusResult, catalogResult, activityResult] = await Promise.allSettled([
        fetchMcpStatus(serverId),
        fetchMcpCatalog(serverId, tenant),
        fetchMcpActivity(serverId, tenant, 50),
      ]);

      setStatus(statusResult.status === 'fulfilled' ? statusResult.value : null);
      setCatalog(catalogResult.status === 'fulfilled' ? catalogResult.value : null);
      setActivity(activityResult.status === 'fulfilled' ? activityResult.value.entries : []);

      const rejected = [statusResult, catalogResult, activityResult].find(
        (result): result is PromiseRejectedResult => result.status === 'rejected'
      );
      if (rejected) {
        setError(
          rejected.reason instanceof Error
            ? rejected.reason.message
            : 'No se pudo contactar con el gateway MCP'
        );
      }
    } finally {
      setLoading(false);
    }
  }, [serverId, tenant]);

  useEffect(() => {
    void load();
  }, [load]);

  const toolColumns = [
    {
      title: 'Tool',
      dataIndex: 'name',
      key: 'name',
      width: 220,
      render: (name: string) => <Text code>{name}</Text>,
    },
    { title: 'Descripción', dataIndex: 'description', key: 'description' },
  ];

  const activityColumns = [
    {
      title: 'Fecha',
      dataIndex: 'ts',
      key: 'ts',
      width: 170,
      render: (ts: string) => new Date(ts).toLocaleString('es-ES'),
    },
    { title: 'Usuario', dataIndex: 'email', key: 'email', width: 220 },
    {
      title: 'Tool',
      dataIndex: 'tool',
      key: 'tool',
      width: 160,
      render: (tool: string) => <Text code>{tool}</Text>,
    },
    ...(serverId === 'brain'
      ? [
          {
            title: 'Resultados',
            dataIndex: 'resultCount',
            key: 'resultCount',
            width: 110,
            render: (count?: number) =>
              count === undefined ? '—' : count === 0 ? <Tag color="orange">0 · sin respuesta</Tag> : count,
          },
          {
            title: 'Relevancia',
            dataIndex: 'topScore',
            key: 'topScore',
            width: 100,
            render: (score?: number) => (score === undefined ? '—' : score.toFixed(2)),
          },
        ]
      : [
          { title: 'Base de datos', dataIndex: 'database', key: 'database', width: 130 },
          { title: 'Colección', dataIndex: 'collection', key: 'collection', width: 130 },
        ]),
    {
      title: 'Duración',
      dataIndex: 'durationMs',
      key: 'durationMs',
      width: 100,
      render: (ms: number) => `${ms} ms`,
    },
    {
      title: 'Estado',
      dataIndex: 'isError',
      key: 'isError',
      width: 90,
      render: (isError: boolean) =>
        isError ? <Tag color="red">Error</Tag> : <Tag color="green">OK</Tag>,
    },
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button icon={<ReloadOutlined />} onClick={() => void load()}>Recargar</Button>
      </div>

      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="No se pudo contactar con el gateway MCP"
          description={error}
        />
      )}

      <Card
        size="small"
        style={{ marginBottom: 16 }}
        title={
          <Space>
            <ApiOutlined />
            <span>{catalog?.server.name ?? (serverId === 'brain' ? 'Brain' : 'MongoDB Atlas')}</span>
            <Tag icon={<SafetyCertificateOutlined />} color="green">Solo lectura</Tag>
            <StatusBadge status={status} serverId={serverId} />
          </Space>
        }
      >
        <Descriptions column={1} size="small" labelStyle={{ width: 220, color: '#8c8c8c' }}>
          <Descriptions.Item label="URL del MCP">
            {catalog ? <Text copyable code>{catalog.connection.mcpUrl}</Text> : <Tag>No disponible</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label="Client ID">
            {catalog ? <Text copyable code>{catalog.connection.clientId}</Text> : <Tag>No disponible</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label="Authorization server">
            {catalog ? <Text copyable code>{catalog.connection.authorizationServer}</Text> : <Tag>No disponible</Tag>}
          </Descriptions.Item>
          <Descriptions.Item label="Organización">
            <Tag color="blue">{tenant}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Dominios permitidos">
            {catalog ? (
              <Space size={4}>
                <Tag color="blue">{catalog.connection.allowedDomain}</Tag>
                <Text type="secondary">vía {catalog.connection.identityProvider ?? 'google'}</Text>
              </Space>
            ) : (
              <Tag>No disponible</Tag>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Versión">
            {status ? `${status.version} · ${catalog?.server.upstream ?? ''}` : '—'}
          </Descriptions.Item>
        </Descriptions>

        <Alert
          type="info"
          showIcon
          style={{ marginTop: 12 }}
          message="El client secret no se muestra aquí"
          description={
            <>
              Vive en Secret Manager (<Text code>mcp-gateway-oauth-client-secret</Text>).
              Recupéralo desde GCP cuando des de alta el conector.
            </>
          }
        />

        <Title level={5} style={{ marginTop: 20 }}>Cómo darlo de alta en Claude</Title>
        <Steps
          direction="vertical"
          size="small"
          current={-1}
          items={[
            {
              title: 'Un admin añade el conector una sola vez',
              description: 'Claude → Settings → Connectors → Add custom connector, con la URL del MCP de arriba.',
            },
            {
              title: 'Configura las credenciales en Advanced settings',
              description: 'Client ID de arriba y el secret de Secret Manager. La configuración de auth no se puede editar después: habría que borrar y volver a crear el conector.',
            },
            {
              title: 'Cada empleado pulsa Connect',
              description: 'Se abre el login de la organización (hoy Google) y solo se acepta una cuenta de sus dominios que además tenga acceso a este servidor. A partir de ahí sus consultas aparecen abajo con su email.',
            },
          ]}
        />
      </Card>

      <Card
        size="small"
        title={`Tools habilitadas (${catalog?.tools.length ?? 0})`}
        style={{ marginBottom: 16 }}
      >
        <Paragraph type="secondary" style={{ marginTop: 0 }}>
          {serverId === 'brain'
            ? 'Todas las tools son de solo lectura: buscan y leen documentos del Brain, no pueden modificarlo.'
            : 'Las operaciones de escritura no están desactivadas en la UI: no llegan a registrarse en el servidor. El build falla si alguna reaparece.'}
        </Paragraph>
        <Table<McpTool>
          size="small"
          rowKey="name"
          pagination={false}
          columns={toolColumns}
          dataSource={catalog?.tools ?? []}
          locale={{ emptyText: 'Sin datos del gateway' }}
        />
      </Card>

      <McpAccessPanel serverId={serverId} tenant={tenant} onChanged={() => void load()} />

      {serverId === 'brain' && <BrainIndexCard tenant={tenant} />}

      {serverId === 'mongodb' && catalog?.connection.database && (
        <McpGuardrailsPanel serverId={serverId} database={catalog.connection.database} />
      )}

      <Card size="small" title="Actividad reciente">
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="Registro temporal, no persistente"
          description="Se guarda en memoria del gateway y se pierde al reiniciar o escalar. El log de auditoría persistente está pendiente de definir."
        />
        <Table<McpActivityEntry>
          size="small"
          rowKey={(record) => `${record.ts}-${record.tool}-${record.email}`}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          columns={activityColumns}
          dataSource={activity}
          locale={{ emptyText: 'Todavía no hay consultas registradas' }}
        />
      </Card>
    </div>
  );
};

export default McpServerDetailPanel;
